<template>
    <div class="qi-page">
        <div class="qi-page-header d-flex justify-content-between align-items-start flex-wrap">
            <div>
                <h4 class="mb-0 text-primary">Invoices</h4>
                <small class="text-muted">Track what customers owe and what's been paid.</small>
            </div>
            <a href="/invoices/create" class="btn btn-primary"><i class="bi bi-plus-lg mr-1"></i> Create Invoice</a>
        </div>

        <b-row v-if="stats" class="mb-3">
            <b-col cols="6" md="3" class="mb-3">
                <div class="qi-stat-card" @click="filterByStatus('')">
                    <div class="qi-stat-card__val text-secondary">{{ stats.total_count }}</div>
                    <div class="qi-stat-card__lbl">Total Invoices</div>
                </div>
            </b-col>
            <b-col cols="6" md="3" class="mb-3">
                <div class="qi-stat-card" @click="filterByStatus('unpaid')">
                    <div class="qi-stat-card__val text-dark">{{ stats.unpaid_count }}</div>
                    <div class="qi-stat-card__lbl">Unpaid</div>
                </div>
            </b-col>
            <b-col cols="6" md="3" class="mb-3">
                <div class="qi-stat-card" @click="filterByStatus('partial')">
                    <div class="qi-stat-card__val text-dark">{{ stats.partial_count }}</div>
                    <div class="qi-stat-card__lbl">Partially Paid</div>
                </div>
            </b-col>
            <b-col cols="6" md="3" class="mb-3">
                <div class="qi-stat-card" @click="filterByStatus('paid')">
                    <div class="qi-stat-card__val text-primary">{{ stats.paid_count }}</div>
                    <div class="qi-stat-card__lbl">Paid</div>
                </div>
            </b-col>
        </b-row>
        <b-row v-if="stats" class="mb-3">
            <b-col md="6" class="mb-3">
                <div class="qi-stat-card qi-stat-card--kpi">
                    <div class="qi-stat-card__lbl mb-1"><i class="bi bi-cash-stack mr-1"></i>Total Outstanding</div>
                    <div class="qi-stat-card__val text-danger">{{ formatMoney(stats.total_outstanding) }}</div>
                </div>
            </b-col>
            <b-col md="6" class="mb-3">
                <div class="qi-stat-card qi-stat-card--kpi">
                    <div class="qi-stat-card__lbl mb-1"><i class="bi bi-receipt mr-1"></i>Total Invoiced</div>
                    <div class="qi-stat-card__val text-dark">{{ formatMoney(stats.total_value) }}</div>
                </div>
            </b-col>
        </b-row>

        <div class="qi-toolbar">
            <input type="text" class="form-control" style="max-width:220px;" placeholder="Search invoice #" v-model="filters.search" @input="debouncedLoad">
            <select class="form-control" style="max-width:170px;" v-model="filters.status" @change="load">
                <option value="">All statuses</option>
                <option v-for="(meta, key) in statuses" :key="key" :value="key">{{ meta.label }}</option>
            </select>
        </div>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <div v-else-if="!invoices.length" class="empty-state">
            <i class="bi bi-receipt" style="font-size:2rem; opacity:.3;"></i>
            <p class="text-muted mt-2 mb-1">No invoices yet</p>
            <small class="text-muted d-block mb-2">Create your first invoice to start tracking customer balances and payments.</small>
            <a href="/invoices/create" class="btn btn-primary"><i class="bi bi-plus-lg mr-1"></i> Create Invoice</a>
        </div>

        <template v-else>
            <div class="table-responsive d-none d-md-block">
                <table class="table table-sm qi-list-table">
                    <thead>
                        <tr>
                            <th>Invoice #</th><th>Customer</th><th>Date</th><th>Due</th>
                            <th class="text-right">Total</th><th class="text-right">Paid</th>
                            <th class="text-right">Balance</th><th>Status</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="inv in invoices" :key="inv.id" class="qi-row" @click="goTo(inv.id)">
                            <td class="font-weight-bold">{{ inv.number }}</td>
                            <td>{{ customerLabel(inv.customer) }}</td>
                            <td>{{ formatDate(inv.date) }}</td>
                            <td>{{ inv.due_date ? formatDate(inv.due_date) : '—' }}</td>
                            <td class="text-right">{{ formatMoney(inv.total_amount) }}</td>
                            <td class="text-right text-primary">{{ formatMoney(inv.paid_amount) }}</td>
                            <td class="text-right" :class="inv.balance > 0 ? 'text-danger' : ''">{{ formatMoney(inv.balance) }}</td>
                            <td><span class="badge" :class="invoiceStatusMeta(inv.status).badge">{{ invoiceStatusMeta(inv.status).label }}</span></td>
                            <td class="text-right"><i class="bi bi-chevron-right text-muted"></i></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-md-none">
                <div class="qi-card-row" v-for="inv in invoices" :key="inv.id" @click="goTo(inv.id)">
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">{{ inv.number }}</span>
                        <span class="badge" :class="invoiceStatusMeta(inv.status).badge">{{ invoiceStatusMeta(inv.status).label }}</span>
                    </div>
                    <div class="text-muted small">{{ customerLabel(inv.customer) }} &middot; {{ formatDate(inv.date) }}</div>
                    <div class="d-flex justify-content-between mt-1">
                        <span class="font-weight-bold">{{ formatMoney(inv.total_amount) }}</span>
                        <span :class="inv.balance > 0 ? 'text-danger' : 'text-primary'">Bal: {{ formatMoney(inv.balance) }}</span>
                    </div>
                </div>
            </div>

            <div v-if="pagination.last_page > 1" class="d-flex justify-content-center mt-3">
                <b-pagination v-model="pagination.current_page" :total-rows="pagination.total" :per-page="pagination.per_page" size="sm" @change="onPage"></b-pagination>
            </div>
        </template>
    </div>
</template>

<script>
import { formatMoney, invoiceStatusMeta, customerLabel, INVOICE_STATUSES } from './docHelpers';

export default {
    name: 'InvoicesList',
    data() {
        return {
            loading: true,
            invoices: [],
            filters: { search: '', status: '' },
            pagination: { current_page: 1, last_page: 1, total: 0, per_page: 20 },
            debounceTimer: null,
            statuses: INVOICE_STATUSES,
            stats: null,
        };
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
    },
    created() { this.load(); this.loadStats(); },
    methods: {
        invoiceStatusMeta, customerLabel,
        formatMoney(v) { return formatMoney(v, this.currency); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
        filterByStatus(status) {
            this.filters.status = status;
            this.pagination.current_page = 1;
            this.load();
        },
        async loadStats() {
            try {
                const { data } = await axios.get('/api/invoices/stats');
                this.stats = data;
            } catch (e) {
                // Stats are a nice-to-have; the list itself still works without them.
            }
        },
        debouncedLoad() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(this.load, 300);
        },
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get('/api/invoices', {
                    params: {
                        search: this.filters.search || undefined,
                        status: this.filters.status || undefined,
                        page: this.pagination.current_page,
                    },
                });
                this.invoices = data.data || [];
                this.pagination = {
                    current_page: data.current_page, last_page: data.last_page,
                    total: data.total, per_page: data.per_page,
                };
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not load invoices.' });
            } finally {
                this.loading = false;
            }
        },
        onPage(page) { this.pagination.current_page = page; this.load(); },
        goTo(id) { window.location.href = `/invoices/${id}`; },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1200px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-page-header { margin-bottom: 14px; }
.qi-toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.qi-list-table th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; padding: 10px 12px; }
.qi-list-table td { padding: 14px 12px; vertical-align: middle; }
.qi-row { cursor: pointer; }
.qi-row:hover { background: #f8f9fa; }
.qi-card-row {
    border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; cursor: pointer;
}
.empty-state { text-align: center; padding: 48px 16px; }

.qi-stat-card {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    background: #fff;
    cursor: pointer;
    transition: box-shadow .15s;
}
.qi-stat-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,.08); }
.qi-stat-card--kpi { cursor: default; text-align: left; }
.qi-stat-card__val { font-size: 28px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
.qi-stat-card__lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #6c757d; }
</style>
